/**
 * LOGOS v5.0 — Monte Carlo Animation (Enhanced with ERS)
 * Symbolic visualization: from one present moment, N futures diverge
 * Enhanced: paths colored by ERS threshold, protocol stats overlay
 */

import React, { useRef, useEffect, useState } from 'react';

const COLORS = {
  bg: '#0F0F1A', border: '#2D2D44', text: '#E0E0F0', textDim: '#8888AA',
  logos: '#C084FC', viability: '#10B981', entropy: '#EF4444',
  ers_high: '#C084FC', ers_low: '#F59E0B',
};

export default function MonteCarloAnimation({ N, ers = 0.5, onComplete }) {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState('running');
  const [currentMessage, setCurrentMessage] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 2;
    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const NUM_VISIBLE = Math.min(N, 60);
    const STEPS = 80;
    const paths = [];

    const seedRand = (i) => {
      let s = i * 9301 + 49297;
      return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
    };

    // Generate paths — ERS influences the drift
    for (let i = 0; i < NUM_VISIBLE; i++) {
      const rng = seedRand(i + Date.now() % 1000);
      const ersBias = (ers - 0.5) * 0.015;
      const drift = (rng() - 0.45) * 0.012 + ersBias;
      const vol = 0.006 + rng() * 0.014;
      const points = [{ x: 0, y: 0.5 }];

      for (let t = 1; t <= STEPS; t++) {
        const prev = points[t - 1];
        const gauss = Math.sqrt(-2 * Math.log(Math.max(0.0001, rng()))) * Math.cos(2 * Math.PI * rng());
        const ny = Math.max(0.02, Math.min(0.98, prev.y + drift + vol * gauss));
        points.push({ x: t / STEPS, y: ny });
      }

      const finalY = points[STEPS].y;
      const isHighPath = finalY > 0.55;
      const hue = isHighPath ? 270 : finalY > 0.42 ? 45 : 0;
      const sat = 70 + rng() * 20;
      const light = 50 + rng() * 15;
      paths.push({
        points,
        color: `hsla(${hue}, ${sat}%, ${light}%, `,
        delay: rng() * 0.3,
        isHighPath,
      });
    }

    const TOTAL_DURATION = 3200;
    const messages = [
      { at: 0.00, text: 'Un solo momento presente...' },
      { at: 0.15, text: 'Los caminos comienzan a divergir...' },
      { at: 0.40, text: `${N.toLocaleString()} futuros posibles desplegándose...` },
      { at: 0.55, text: ers > 0.5 ? 'ERS alto — más caminos ascienden ✦' : 'Observa hacia dónde gravitan los caminos...' },
      { at: 0.70, text: 'Algunos ascienden. Otros descienden.' },
      { at: 0.88, text: 'La probabilidad habla...' },
    ];

    let startTime = null;

    function draw(timestamp) {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const t = Math.min(1, elapsed / TOTAL_DURATION);

      const msg = [...messages].reverse().find((m) => t >= m.at);
      if (msg) setCurrentMessage(msg.text);

      ctx.fillStyle = COLORS.bg;
      ctx.fillRect(0, 0, W, H);

      // Subtle grid
      ctx.strokeStyle = COLORS.border + '40';
      ctx.lineWidth = 0.3;
      for (let gx = 0; gx < W; gx += 30) {
        ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke();
      }
      for (let gy = 0; gy < H; gy += 30) {
        ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke();
      }

      // ERS threshold reference line
      const ersY = H * (1 - 0.60);
      ctx.strokeStyle = ers > 0.5 ? COLORS.ers_high + '40' : COLORS.ers_low + '40';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 6]);
      ctx.beginPath(); ctx.moveTo(0, ersY); ctx.lineTo(W, ersY); ctx.stroke();
      ctx.setLineDash([]);

      // Origin glow
      const originX = W * 0.06;
      const originY = H * 0.5;
      const glowR = 8 + Math.sin(elapsed * 0.005) * 3;
      const originGrad = ctx.createRadialGradient(originX, originY, 0, originX, originY, glowR * 3);
      originGrad.addColorStop(0, COLORS.logos + '90');
      originGrad.addColorStop(0.5, COLORS.logos + '30');
      originGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = originGrad;
      ctx.fillRect(originX - 30, originY - 30, 60, 60);
      ctx.beginPath();
      ctx.arc(originX, originY, glowR * 0.5, 0, Math.PI * 2);
      ctx.fillStyle = COLORS.logos;
      ctx.fill();

      // Draw paths
      const drawProgress = Math.min(1, t / 0.65);
      for (const path of paths) {
        const pathT = Math.max(0, Math.min(1, (drawProgress - path.delay) / (1 - path.delay)));
        if (pathT <= 0) continue;
        const pointsToShow = Math.floor(pathT * STEPS);

        ctx.beginPath();
        for (let j = 0; j <= pointsToShow; j++) {
          const px = W * 0.06 + path.points[j].x * W * 0.88;
          const py = H * (1 - path.points[j].y);
          j === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.strokeStyle = path.color + (0.3 + pathT * 0.4).toFixed(2) + ')';
        ctx.lineWidth = 1.2;
        ctx.stroke();

        if (pathT > 0.95) {
          const last = path.points[pointsToShow];
          const ex = W * 0.06 + last.x * W * 0.88;
          const ey = H * (1 - last.y);
          ctx.beginPath();
          ctx.arc(ex, ey, 2, 0, Math.PI * 2);
          ctx.fillStyle = path.color + '0.8)';
          ctx.fill();
        }
      }

      // ERS badge
      ctx.fillStyle = ers > 0.5 ? COLORS.ers_high : COLORS.ers_low;
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(`ERS: ${ers.toFixed(3)}`, W - 10, 18);

      // Stats overlay at end
      if (t > 0.92) {
        const highPaths = paths.filter((p) => p.isHighPath).length;
        const pct = ((highPaths / NUM_VISIBLE) * 100).toFixed(0);

        ctx.fillStyle = 'rgba(15,15,26,0.75)';
        ctx.fillRect(W * 0.3, H * 0.38, W * 0.4, H * 0.24);
        ctx.strokeStyle = COLORS.logos + '60';
        ctx.lineWidth = 1;
        ctx.strokeRect(W * 0.3, H * 0.38, W * 0.4, H * 0.24);

        ctx.fillStyle = COLORS.text;
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`${pct}% ascendentes`, W * 0.5, H * 0.49);
        ctx.font = '10px Arial';
        ctx.fillStyle = COLORS.textDim;
        ctx.fillText(`de ${N.toLocaleString()} trayectorias simuladas`, W * 0.5, H * 0.56);
      }

      if (t < 1) {
        requestAnimationFrame(draw);
      } else {
        setPhase('done');
        setTimeout(() => onComplete && onComplete(), 800);
      }
    }

    const animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, [N, ers]);

  return (
    <div style={{ position: 'relative' }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%', height: '220px', borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
        }}
      />
      {currentMessage && (
        <div style={{
          position: 'absolute', bottom: '12px', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(15,15,26,0.85)', borderRadius: '20px', padding: '6px 16px',
          fontSize: '12px', color: COLORS.textDim, whiteSpace: 'nowrap',
          fontStyle: 'italic',
        }}>
          {currentMessage}
        </div>
      )}
    </div>
  );
}
